export const content = {
  en: {
    nav: { services: "Services", approach: "Approach", about: "About", experience: "Experience", contact: "Contact", cta: "Book a call" },
    hero: {
      eyebrow: "Certified Trainer · Business Moderator · Lecturer",
      lines: ["Better conversations.", "Better decisions."],
      lead: "I design and facilitate structured workshops that help leadership teams make better decisions and align around a clear direction.",
      primary: "Book an introductory call",
      secondary: "See how I work",
      meta: [["10+ yrs", "facilitating workshops"], ["4+ yrs", "project experience\nas a consultant"], ["2+ yrs", "lecturer at TH Ingolstadt"]],
    },
    marquee: ["Leadership", "Strategy", "Change", "Collaboration", "Facilitation", "Alignment", "Decision-Making"],
    trusted: { label: "Selected engagements with" },
    services: {
      eyebrow: "Focus Areas",
      title: "For the topics that deserve focused attention.",
      lead: "Every session is designed from the outcome backwards. These are the settings I most often work in.",
      items: [
        ["01", "Leadership Development", "Formats that let leaders reflect, practise and align on how they lead — grounded in organizational psychology."],
        ["02", "Team Collaboration", "Sessions that surface how a team really works together and where friction quietly costs momentum."],
        ["03", "Organizational Change", "Facilitation through transformation and change, keeping people oriented and involved."],
        ["04", "Strategy Workshops", "Structured decision-making that turns complex debate into a shared, committed direction."],
      ],
    },
    approach: {
      eyebrow: "How I work",
      title: "The best answers come from thinking together.",
      chapters: [
        ["01", "Practice over theory", "Real-world challenges come first. I structure the process and keep the conversation on track."],
        ["02", "Make complexity manageable", "I bring different perspectives together and create clarity around complex topics."],
        ["03", "Answers emerge from the team", "Neutral facilitation creates space for everyone to contribute and for the team to develop a shared solution."],
        ["04", "Concrete next steps", "Conversations lead to clear next steps the team can act on right away."],
      ],
    },
    about: {
      eyebrow: "About",
      title: "Timo Schewik",
      role: "Business psychologist",
      body: [
        "As a business psychologist, I work independently as a consultant and facilitator. I support teams in medium-sized and large organizations in creating clarity together and developing decisions they can genuinely stand behind.",
        "My work brings together business psychology, leadership and change. I spent four years in management consulting at P3 Group and now also teach at TH Ingolstadt. But my real craft is creating the space for good conversations — conversations that lead to clarity, shared decisions and concrete next steps.",
      ],
      quote: "\u201CI don't present ready-made answers. I create an environment where teams develop their own — and align around a common direction.\u201D",
    },
    experience: {
      eyebrow: "Experience & Credentials",
      title: "Proven in practice.",
      background: {
        title: "Background",
        items: [
          "10 years facilitating interactive workshops & trainings",
          "4 years in management consulting (P3 Group)",
          "Lecturer at TH Ingolstadt",
          "Academic background in business psychology",
        ],
      },
      education: {
        title: "Education",
        items: [
          "M.Sc. Psychology & Management — ISM Munich",
          "M.Sc. Management — Edinburgh Napier (double degree)",
          "B.Sc. Geography & Economics — LMU Munich",
        ],
      },
      certificates: {
        title: "Certificates",
        items: [
          "Certified Professional Trainer (BDVT e.V.)",
          "Systemic Coaching & Change Management (ISB)",
          "Certified SAFe® SPC",
          "Professional Scrum Master (PSM I)",
        ],
      },
      languages: {
        title: "Languages",
        items: ["German — native", "English — business fluent", "Spanish — fluent"],
      },
    },
    contact: {
      eyebrow: "Let's talk",
      title: "Book an introductory call.",
      lead: "Share a few details, and I'll get back to you within two business days.",
      name: "Name", email: "Email", org: "Organization", orgOpt: "(optional)", message: "Your enquiry",
      send: "Send request", sending: "Sending…",
      success: "Thank you — your message is on its way. I'll be in touch shortly.",
      error: "Something went wrong. Please try again or email me directly.",
      directLabel: "Or reach me directly",
    },
    footer: { tagline: "Independent facilitation for leadership, strategy and change.", rights: "All rights reserved.", impressum: "Imprint", datenschutz: "Privacy" },
  },
  de: {
    nav: { services: "Angebot", approach: "Arbeitsweise", about: "Über mich", experience: "Erfahrung", contact: "Kontakt", cta: "Gespräch vereinbaren" },
    hero: {
      eyebrow: "Zertifizierter Trainer · Business Moderator · Dozent",
      lines: ["Gute Entscheidungen", "entstehen durch", "gute Gespräche."],
      lead: "Ich gestalte und moderiere Workshops, in denen Führungsteams gemeinsam bessere Entscheidungen treffen und zu einer klaren Richtung finden.",
      primary: "Gespräch vereinbaren",
      secondary: "Meine Arbeitsweise",
      meta: [["10+ Jahre", "Workshop-Moderation"], ["4+ Jahre", "Projekterfahrung\nals Unternehmensberater"], ["2+ Jahre", "Dozent an der TH Ingolstadt"]],
    },
    marquee: ["Führung", "Strategie", "Veränderung", "Zusammenarbeit", "Moderation", "Orientierung", "Entwicklung"],
    trusted: { label: "Zusammenarbeit u. a. mit" },
    services: {
      eyebrow: "Was ich moderiere",
      title: "Für die Themen, die nicht nebenbei geklärt werden.",
      lead: "Ein guter Workshop beginnt mit dem Ziel. Das Format richtet sich danach – nicht umgekehrt.",
      items: [
        ["01", "Führungskräfte-Entwicklung", "Formate, in denen Führungskräfte ihr eigenes Führungsverhalten reflektieren, ausprobieren und schärfen – fundiert aus der Wirtschaftspsychologie."],
        ["02", "Teamdynamik & Zusammenarbeit", "Gemeinsam sichtbar machen, was im Team gut funktioniert, wo Reibung entsteht und was sich verändern soll."],
        ["03", "Transformation & Change", "Begleitung in Veränderungssituationen – von neuen Strukturen und Rollen über agile Arbeitsweisen bis zu technologischen Veränderungen wie KI."],
        ["04", "Strategie-Workshops", "Strategische Themen gemeinsam sortieren, Prioritäten klären und eine tragfähige Richtung entwickeln."],
      ],
    },
    approach: {
      eyebrow: "Meine Arbeitsweise",
      title: "Die besten Antworten entstehen selten allein.",
      chapters: [
        ["01", "Praxis vor Theorie", "Im Mittelpunkt stehen reale Herausforderungen aus dem Arbeitsalltag. Ich strukturiere den Prozess und halte den roten Faden."],
        ["02", "Komplexität ordnen", "Ich führe unterschiedliche Perspektiven zusammen und bringe so Ordnung in komplexe Themen."],
        ["03", "Raum für eigene Lösungen", "Ich moderiere neutral und schaffe die Bedingungen, unter denen das Team eigene Lösungen entwickeln kann."],
        ["04", "Konkrete nächste Schritte", "Aus den Gesprächen entstehen konkrete nächste Schritte, die direkt anschlussfähig für den Arbeitsalltag sind."],
      ],
    },
    about: {
      eyebrow: "Über mich",
      title: "Timo Schewik",
      role: "Wirtschaftspsychologe",
      body: [
        "Als Wirtschaftspsychologe arbeite ich unabhängig als Berater und Moderator. Ich begleite Teams in mittleren und großen Organisationen dabei, gemeinsam Klarheit zu schaffen und tragfähige Entscheidungen zu entwickeln.",
        "Meine Arbeit verbindet Wirtschaftspsychologie, Führung und Veränderung. Vier Jahre war ich in der Unternehmensberatung bei der P3 Group, heute bin ich zudem Lehrbeauftragter an der TH Ingolstadt. Mein eigentliches Handwerk aber ist der Raum: Gespräche so zu gestalten, dass am Ende Klarheit, gemeinsame Entscheidungen und konkrete nächste Schritte stehen.",
      ],
      quote: "\u201EIch liefere keine fertigen Antworten. Ich schaffe einen Rahmen, in dem Teams ihre eigenen entwickeln – und sich auf eine gemeinsame Richtung einigen.\u201C",
    },
    experience: {
      eyebrow: "Erfahrung & Qualifikationen",
      title: "Fundiert, belegt und in der Praxis erprobt.",
      background: {
        title: "Hintergrund",
        items: [
          "10 Jahre Moderation interaktiver Workshops & Trainings",
          "4 Jahre Unternehmensberatung (P3 Group)",
          "Lehrbeauftragter an der TH Ingolstadt",
          "Wissenschaftlicher Hintergrund in der Wirtschaftspsychologie",
        ],
      },
      education: {
        title: "Ausbildung",
        items: [
          "M.Sc. Psychology & Management — ISM München",
          "M.Sc. Management — Edinburgh Napier (Double Degree)",
          "B.Sc. Geographie & VWL — LMU München",
        ],
      },
      certificates: {
        title: "Fortbildungen",
        items: [
          "Zertifizierter Fachtrainer (BDVT e.V.)",
          "Systemisches Coaching & Veränderungsmanagement (ISB)",
          "Zertifizierter SAFe® SPC",
          "Professional Scrum Master (PSM I)",
        ],
      },
      languages: {
        title: "Sprachen",
        items: ["Deutsch — Muttersprache", "Englisch — verhandlungssicher", "Spanisch — fließend"],
      },
    },
    contact: {
      eyebrow: "Kontakt",
      title: "Gerne stehe ich für ein unverbindliches Erstgespräch zur Verfügung.",
      lead: "Über ein paar kurze Stichpunkte zum Anliegen würde ich mich sehr freuen. Eine Rückmeldung erfolgt persönlich innerhalb von zwei Werktagen.",
      name: "Name", email: "E-Mail", org: "Organisation", orgOpt: "(optional)", message: "Anliegen",
      send: "Anfrage senden", sending: "Wird gesendet…",
      success: "Vielen Dank – Ihre Nachricht ist unterwegs. Ich melde mich in Kürze bei Ihnen.",
      error: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder schreiben Sie mir direkt.",
      directLabel: "Oder direkt Kontakt aufnehmen",
      privacyNote: "Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben zur Bearbeitung Ihrer Anfrage zu.",
      privacyLink: "Datenschutzerklärung",
    },
    footer: { tagline: "Unabhängige Moderation für Führung, Strategie und Zusammenarbeit.", rights: "Alle Rechte vorbehalten.", impressum: "Impressum", datenschutz: "Datenschutz" },
  },
};

export const CONTACT = {
  email: "kontakt@schewik.com",
  phone: "+49 176 23763529",
  phoneHref: "tel:+4917623763529",
  linkedin: "https://www.linkedin.com/in/timo-schewik",
  name: "Timo Schewik",
};

export const CLIENTS = ["BMW", "Volkswagen", "Porsche", "Mercedes-Benz", "Henkel", "Bosch", "P3 Group", "TH Ingolstadt"];

export const CLIENT_LOGOS = [
  { name: "BMW", file: "bmw" },
  { name: "Volkswagen", file: "volkswagen" },
  { name: "Audi", file: "audi", cls: "h-9 md:h-12" },
  { name: "Porsche", file: "porsche" },
  { name: "Bosch", file: "bosch", cls: "h-10 md:h-[3.25rem]" },
  { name: "Henkel", file: "henkel", cls: "h-11 md:h-[3.75rem]" },
  { name: "P3 Group", file: "p3", cls: "h-10 md:h-[3.25rem]" },
  { name: "TH Ingolstadt", file: "th-ingolstadt" },
];
